import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { theme } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { RootState } from 'store/configureStore';
import {
  RiskMaintenance,
  RiskMaintenanceTableRow
} from 'store/ducks/Reporting/types';
import CustomTable from 'components/Tables/CustomTable';
import ApiService from 'utils/ApiCalls';
import * as Styled from 'components/Layouts/Layout.styled';
import * as LocalStyled from './RiskMaintenance.styled';
import * as Constants from './RiskMaintenance.constants';
import * as ReportingStyled from '../Reporting.styled';
import { useReportExport } from 'utils/hooks/useReportExport';

const { useToken } = theme;

const RiskMaintenanceReport: React.FC = () => {
  const { token } = useToken();

  const [riskData, setRiskData] = useState<RiskMaintenanceTableRow[]>([]);

  const [loading, setLoading] = useState(false);

  const [selectedDate, setSelectedDate] = useState<dayjs.Dayjs | null>(null);

  const [shouldFetch, setShouldFetch] = useState(false);

  const [searchValues] = useState<Record<string, string>>({});

  const { scheme } = useSelector((state: RootState) => ({
    scheme: state.global.scheme
  }));

  const { registerReport, clearReport } = useReportExport();

  const filteredRiskData = useMemo(() => {
    if (!Object.values(searchValues).some(Boolean)) {
      return riskData;
    }

    return riskData.filter((record) => {
      return Object.entries(searchValues).every(([field, searchValue]) => {
        if (!searchValue) {
          return true;
        }

        const recordValue = record[field as keyof RiskMaintenanceTableRow];

        return String(recordValue ?? '')
          .toLowerCase()
          .includes(searchValue.toLowerCase());
      });
    });
  }, [riskData, searchValues]);

  const columns = useMemo<ColumnsType<RiskMaintenanceTableRow>>(
    () =>
      Constants.processRiskMaintenanceColumns(
        riskData
      ) as unknown as ColumnsType<RiskMaintenanceTableRow>,
    [riskData]
  );

  /**
   * Register current table data for Excel export.
   */
  useEffect(() => {
    registerReport(
      filteredRiskData,
      columns as unknown as any[],
      'Risk_Maintenance',
      'Risk Maintenance'
    );
  }, [filteredRiskData, columns, registerReport]);

  /**
   * Clear report when leaving page.
   */
  useEffect(() => {
    return () => {
      clearReport();
    };
  }, [clearReport]);

  /**
   * Fetch risk maintenance data.
   */
  useEffect(() => {
    if (!shouldFetch) {
      return;
    }

    const fetchRisks = async () => {
      setLoading(true);

      try {
        const response = await ApiService.getRiskMaintenance(
          selectedDate?.format('YYYY-MM-DD')
        );

        if (
          response.data?.status === 'success' &&
          Array.isArray(response.data.data)
        ) {
          const rows: RiskMaintenanceTableRow[] = response.data.data.flatMap(
            (risk: RiskMaintenance) =>
              risk.instances.map((instance) => ({
                key: `${risk.id}_${instance.instance_id}`,
                group_name: instance.group?.name ?? null,
                division_name: instance.division?.name ?? null,
                region_name: instance.region?.name ?? null,
                legal_entity_name: instance.legal_entity?.name ?? null,
                business_unit_name: instance.business_unit?.name ?? null,
                risk_title: risk.title ?? null,
                risk_instance_severity_name: instance.severity?.name ?? null,
                risk_instance_probability_of_occurrence_name:
                  instance.probability_of_occurrence?.name ?? null,
                risk_status_name: instance.risk_status?.name ?? null,
                risk_assessment_score: instance.risk_assessment_score ?? null,
                speed_of_occurrence_name:
                  instance.speed_of_occurrence?.name ?? null,
                gross_risk: instance.gross_risk ?? null,
                residual_risk: instance.residual_risk ?? null,
                risk_owner: instance.risk_owner ?? null,
                risk_trend_name: instance.risk_trend?.name ?? null,
                owner_maintainer: instance.owner_maintainer ?? null,
                assessment_date: instance.assessment_date ?? null,
                update_cycle_name: instance.update_cycle?.name ?? null,
                impact_consequence: instance.impact_consequence ?? null,
                start_date: instance.start_date ?? null,
                end_date: instance.end_date ?? null
              }))
          );

          setRiskData(rows);
        } else {
          setRiskData([]);
        }
      } catch (error) {
        console.error('Failed to fetch risk maintenance:', error);

        setRiskData([]);
      } finally {
        setLoading(false);
        setShouldFetch(false);
      }
    };

    fetchRisks();
  }, [shouldFetch, selectedDate]);

  const handleDisplay = () => {
    setShouldFetch(true);
  };

  return (
    <>
      <LocalStyled.GlobalDatePickerStyle token={token} />

      <ReportingStyled.ReportTableWrapper>
        <LocalStyled.RisksControlsGapsFilterBar>
          <Styled.HorizontalFilterBar token={token} scheme={scheme}>
            <Styled.FilteredContainerRisks>
              <LocalStyled.StyledDatePicker
                placeholder='Date'
                value={selectedDate}
                onChange={(date) => setSelectedDate(date as dayjs.Dayjs | null)}
                popupClassName='white-bg-datepicker'
                getPopupContainer={(trigger) =>
                  trigger.parentElement || document.body
                }
              />

              <Styled.Spacer />

              <Styled.StyledButton
                type='primary'
                onClick={handleDisplay}
                className='display-save-button'
              >
                Display
              </Styled.StyledButton>
            </Styled.FilteredContainerRisks>
          </Styled.HorizontalFilterBar>
        </LocalStyled.RisksControlsGapsFilterBar>

        <LocalStyled.RisksControlsGapsTableWrapper>
          <CustomTable
            drawTable
            itemsCount={filteredRiskData.length}
            columns={columns}
            dataSource={filteredRiskData}
            loading={loading}
            rowKey='key'
            scroll={{
              x: 4000,
              y: 350
            }}
            sticky
            scheme={scheme}
          />
        </LocalStyled.RisksControlsGapsTableWrapper>
      </ReportingStyled.ReportTableWrapper>
    </>
  );
};

export default RiskMaintenanceReport;
